import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { appConfig } from '@/lib/app-config';
import type { OnboardingSummary, User } from '../api/auth';
import * as authApi from '../api/auth';
import { clearToken, getToken } from '../api/client';

function isEmployeeWebRole(role: string): boolean {
  return role === 'EMPLOYEE' || role === 'MANAGER';
}

interface AuthState {
  user: User | null;
  onboarding: OnboardingSummary | null;
  loading: boolean;
  checked: boolean;
}

const AuthContext = createContext<{
  user: User | null;
  onboarding: OnboardingSummary | null;
  loading: boolean;
  checked: boolean;
  login: (email: string, password: string, remember?: boolean) => Promise<{ error?: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
} | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({ user: null, onboarding: null, loading: true, checked: false });

  const refreshUser = useCallback(async () => {
    const res = await authApi.me();
    if (res.data?.user) setState((s) => ({ ...s, user: res.data!.user, onboarding: res.data!.onboarding ?? null, checked: true }));
    else setState((s) => ({ ...s, user: null, onboarding: null, checked: true }));
  }, []);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setState((s) => ({ ...s, loading: false, checked: true }));
      return;
    }
    authApi.me().then((res) => {
      if (res.data?.user) {
        if (appConfig.isEmployeeWeb && !isEmployeeWebRole(res.data.user.role)) {
          clearToken();
          setState((s) => ({ ...s, user: null, onboarding: null, loading: false, checked: true }));
          return;
        }
        setState((s) => ({ ...s, user: res.data!.user, onboarding: res.data!.onboarding ?? null, loading: false, checked: true }));
      } else {
        clearToken();
        setState((s) => ({ ...s, user: null, onboarding: null, loading: false, checked: true }));
      }
    });
  }, []);

  const login = useCallback(async (email: string, password: string, remember = true) => {
    try {
      const res = await authApi.login(email, password, remember);
      if (res.error) return { error: res.error };
      if (res.data?.user) {
        if (appConfig.isEmployeeWeb && !isEmployeeWebRole(res.data.user.role)) {
          await authApi.logout();
          return { error: 'HR and admin accounts sign in at /admin.' };
        }
        setState((s) => ({ ...s, user: res.data!.user, onboarding: res.data!.onboarding ?? null, loading: false, checked: true }));
        return {};
      }
      return { error: 'Login failed' };
    } catch (e) {
      return { error: e instanceof Error ? e.message : 'Login failed' };
    }
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    setState({ user: null, onboarding: null, loading: false, checked: true });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user: state.user,
        onboarding: state.onboarding,
        loading: state.loading,
        checked: state.checked,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
